/**
 * Owner: Masa Bayu
 * File: AuthorProfilePanels.tsx
 * Date:Time: 2026-10-23 00:11
 * Description: §412 — panel tab Author Detail: Books (senarai nombor buku
 *   terbitan + kad ringkasan + Similar Authors), About (bio, genre
 *   chips wired ke /all-books, kontak = URL canonical), Reviews (kosong
 *   jujur). Butang Read → /read/:id.
 *   §466 S1 — penarafan/lencana hash dan kiraan pengikut dibuang: baris buku
 *   paparkan harga sebenar hub; ringkasan kedua = bilangan genre daripada
 *   katalog; emel `.@qxread.com` yang dijana dan lencana "DEMO" dibuang sebab
 *   tiada apa yang direka untuk ditanda.
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { qxCategoryName } from '@qx/qxread-core';
import { LandingIcon } from '../../home/marketplace/landing/LandingIcons';
import { formatMarketplacePriceOrUnknown } from '../../home/marketplace/marketplace-locale';
import { listMarketplaceBooks } from '../../home/marketplace/marketplace-projection';
import { byRecencyDesc, listPublicAuthors, type PublicAuthor } from './public-author-data';
import { similarAuthors } from '../discovery/author-discovery-data';
import { AuthorAvatar } from '../discovery/AuthorAvatar';
import type { AuthorScreensLabels } from '../discovery/author-discovery-labels';
import './author-profile-panels.css';

type Language = 'en' | 'ms';

interface PanelProps {
  readonly author: PublicAuthor;
  readonly t: AuthorScreensLabels;
  readonly language: Language;
  readonly onNavigate: (path: string) => void;
}

/* ---------- Books ---------- */

export function AuthorBooksPanel({ author, t, language, onNavigate }: PanelProps): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const sorted = useMemo(() => [...author.books].sort(byRecencyDesc), [author]);
  const shown = expanded ? sorted : sorted.slice(0, 5);
  const similar = useMemo(
    () => similarAuthors(listPublicAuthors(listMarketplaceBooks()), author),
    [author],
  );
  return (
    <div className="qxapf__stack">
      <div className="qxapf__subhead">
        <h2 className="qxapf__h2">{t.profile.latestBooks}</h2>
        {sorted.length > 5 ? (
          <button type="button" className="qxapf__viewall" onClick={() => setExpanded((p) => !p)}>
            {t.profile.viewAll}
            <LandingIcon name="arrowRight" size={14} />
          </button>
        ) : null}
      </div>
      {shown.length === 0 ? (
        <p className="qxapf__empty">{t.profile.noBooks}</p>
      ) : (
        <ol className="qxapf__plist" role="list" aria-label={t.profile.booksListAria}>
          {shown.map((book, index) => (
            <li key={book.publicationId} className="qxapf__prow">
              <span className="qxapf__prank">{index + 1}</span>
              <button
                type="button"
                className="qxapf__pcover"
                data-tone={book.coverTone}
                aria-label={book.title}
                onClick={() => {
                  onNavigate(`/publication/${book.publicationId}`);
                }}
              >
                <span className="qxapf__pcover-title">{book.title}</span>
              </button>
              <span className="qxapf__ptext">
                <button
                  type="button"
                  className="qxapf__ptitle"
                  onClick={() => {
                    onNavigate(`/publication/${book.publicationId}`);
                  }}
                >
                  {book.title}
                </button>
                {book.categoryId === null ? null : (
                  <span className="qxapf__pgenre">{qxCategoryName(book.categoryId, language)}</span>
                )}
                <span className="qxapf__pmeta">
                  <span className="qxapf__preads">
                    {formatMarketplacePriceOrUnknown(
                      book.priceMinorUnits,
                      book.currency,
                      t.profile.priceUnknown,
                    )}
                  </span>
                </span>
              </span>
              <button
                type="button"
                className="qxapf__read"
                onClick={() => {
                  onNavigate(`/read/${encodeURIComponent(book.publicationId)}`);
                }}
              >
                {t.profile.read}
              </button>
            </li>
          ))}
        </ol>
      )}

      <div className="qxapf__summary" aria-label={t.profile.totalBooks}>
        <span className="qxapf__sum-cell">
          <span className="qxapf__sum-label">{t.profile.totalBooks}</span>
          <span className="qxapf__sum-num">{author.books.length}</span>
        </span>
        <span className="qxapf__sum-cell">
          <span className="qxapf__sum-label">{t.profile.categoriesStat}</span>
          <span className="qxapf__sum-num">{author.genres.length}</span>
        </span>
      </div>

      <div className="qxapf__subhead">
        <h2 className="qxapf__h2">{t.profile.similarHeading}</h2>
        <button
          type="button"
          className="qxapf__viewall"
          onClick={() => {
            onNavigate('/authors');
          }}
        >
          {t.profile.viewAll}
          <LandingIcon name="arrowRight" size={14} />
        </button>
      </div>
      {similar.length === 0 ? (
        <p className="qxapf__empty">{t.profile.noSimilar}</p>
      ) : (
        <ul className="qxapf__simgrid" role="list" aria-label={t.profile.similarAria}>
          {similar.map((other) => (
            <li key={other.id}>
              <button
                type="button"
                className="qxapf__sim-hit"
                onClick={() => {
                  onNavigate(`/author/${other.id}`);
                }}
              >
                <span className="qxapf__sim-avatar">
                  <AuthorAvatar initials={other.initials} seed={other.id} />
                </span>
                <span className="qxapf__sim-name">{other.name}</span>
                <span className="qxapf__sim-count">
                  {`${other.books.length} ${t.profile.booksStat}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------- About ---------- */

export function AuthorAboutPanel({ author, t, language, onNavigate }: PanelProps): ReactElement {
  return (
    <div className="qxapf__stack">
      <h2 className="qxapf__h2">{t.profile.bioHeading}</h2>
      {author.bio === null ? (
        <p className="qxapf__empty">{t.profile.bioEmpty}</p>
      ) : (
        <p className="qxapf__about-bio">{author.bio}</p>
      )}

      <h2 className="qxapf__h2">{t.profile.genresHeading}</h2>
      {author.genres.length === 0 ? (
        <p className="qxapf__empty">{t.profile.noGenres}</p>
      ) : (
        <ul className="qxapf__chips" role="list">
          {author.genres.map((genre) => (
            <li key={genre}>
              <button
                type="button"
                className="qxapf__chip"
                onClick={() => {
                  onNavigate(`/all-books?category=${genre}`);
                }}
              >
                {qxCategoryName(genre, language)}
              </button>
            </li>
          ))}
        </ul>
      )}

      <h2 className="qxapf__h2">{t.profile.contactHeading}</h2>
      <dl className="qxapf__contact">
        <div>
          <dt>{t.profile.websiteLabel}</dt>
          <dd>
            {/* URL canonical profil ini — pautan sebenar, bukan web luar rekaan. */}
            <a href={author.website} target="_blank" rel="noreferrer">
              {author.website.replace(/^https?:\/\//, '')}
            </a>
          </dd>
        </div>
      </dl>
    </div>
  );
}

/* ---------- Reviews ---------- */

export function AuthorReviewsPanel({
  t,
}: Omit<PanelProps, 'author' | 'language' | 'onNavigate'>): ReactElement {
  return (
    <div className="qxapf__stack">
      <h2 className="qxapf__h2">{t.profile.reviewsHeading}</h2>
      {/* §466 S1 — senarai ulasan rekaan dibuang; hub belum ada endpoint penilaian. */}
      <p className="qxapf__empty">{t.profile.reviewsEmpty}</p>
    </div>
  );
}
